import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { Shield, Smartphone, Key, Loader2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = window.__BACKEND_URL__;
const API = `${BACKEND_URL}/api`;

const TwoFactorLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const { login } = useAuth();
  const [code, setCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);

  // Get email + pre-auth token from location state (passed from SignIn page).
  // The pre-auth token is NOT a session — the backend only issues the real
  // JWT after the TOTP code verifies (Jun 2026 security fix).
  const email = location.state?.email;
  const preAuthToken = location.state?.preAuthToken;

  if (!email || !preAuthToken) {
    navigate('/signin');
    return null;
  }

  const handleVerify = async (e) => {
    e.preventDefault();
    
    if (code.length < 6) {
      toast({
        title: 'Invalid Code',
        description: useBackupCode ? 'Enter your backup code' : 'Please enter a 6-digit code',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);
    try {
      const response = await axios.post(`${API}/2fa/verify-login`, {
        pre_auth_token: preAuthToken,
        code,
      });

      if (response.data.token) {
        // Complete login — the real session token only exists now
        login(response.data.token, response.data.user, response.data.company);

        toast({
          title: 'Success',
          description: 'Login successful!',
        });

        const r = response.data.user?.role;
        navigate(r === 'admin' || r === 'supervisor' ? '/dashboard' : '/clock-in');
      }
    } catch (error) {
      toast({
        title: 'Verification Failed',
        description: error.response?.data?.detail || 'Invalid code. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-white to-green-50 p-4">
      <Card className="w-full max-w-md shadow-2xl">
        <CardHeader className="space-y-4 text-center bg-gradient-to-r from-purple-600 to-blue-600 text-white rounded-t-lg">
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
              <Shield className="w-10 h-10 text-purple-600" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl font-bold">Two-Factor Authentication</CardTitle>
            <CardDescription className="text-purple-100 text-base mt-2">
              Enter the code from your authenticator app
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="p-8">
          <form onSubmit={handleVerify} className="space-y-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start space-x-3">
              <Smartphone className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-blue-900">
                  Open Microsoft Authenticator
                </p>
                <p className="text-xs text-blue-700 mt-1">
                  Enter the 6-digit code shown for {email}
                </p>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="code" className="text-center block">
                {useBackupCode ? 'Backup Code' : 'Verification Code'}
              </Label>
              <Input
                id="code"
                type="text"
                maxLength={useBackupCode ? 9 : 6}
                placeholder={useBackupCode ? '0000-0000' : '000000'}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/[^0-9-]/g, ''))}
                className="text-center text-3xl font-mono tracking-wider h-16"
                autoFocus
              />
            </div>

            <Button
              type="submit"
              disabled={isLoading || code.length < 6}
              className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 h-12 text-lg"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                'Verify Code'
              )}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={() => {
                  setUseBackupCode(!useBackupCode);
                  setCode('');
                }}
                className="text-sm text-blue-600 hover:underline"
              >
                <Key className="w-4 h-4 inline mr-1" />
                {useBackupCode ? 'Use authenticator code instead' : 'Use backup code instead'}
              </button>
            </div>

            <div className="text-center pt-4 border-t">
              <button
                type="button"
                onClick={() => navigate('/signin')}
                className="text-sm text-gray-600 hover:underline"
              >
                Back to Sign In
              </button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default TwoFactorLogin;
